/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, type DragEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FsiType } from '../../constants/fsi'
import { getDetails } from '../../services/api/files'
import { toast } from '../../services/toast'
import { useModal } from '../../contexts/ModalContext'
import PageWrapper from './edit-document/PageWrapper'

interface FsiProp {
  id: number
  type: string
}

interface DragNDropFileToPlaceholderProps {
  fsi: FsiProp
  className?: string
  title?: string
  children?: ReactNode
  isTableCell?: boolean
}

const isFileDrag = (dragEvent: any): boolean => {
  const dataTransfer = dragEvent.dataTransfer || dragEvent?.originalEvent?.dataTransfer
  return dataTransfer && dataTransfer.types ? !!dataTransfer.types.find((t: string) => t === 'Files') : false
}

function DragNDropFileToPlaceholder({ fsi, className, title, children, isTableCell }: DragNDropFileToPlaceholderProps) {
  const { t } = useTranslation()
  const { showModal, hideModal } = useModal()
  const [dragging, setDragging] = useState(false)
  const [dndProps, setDndProps] = useState<Record<string, (e: DragEvent<HTMLElement>) => void>>({})

  useEffect(() => {
    setDndProps(
      fsi?.type !== FsiType.PLACEHOLDER
        ? {}
        : {
            onDrop: handleNewFileUpload,
            onDragOver: handleDrag,
            onDragLeave: handleDragOut,
            onDragEnter: handleDragIn
          }
    )
  }, [fsi])

  const handleDrag = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragging(true)
  }

  const handleDragIn = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (isFileDrag(e)) {
      setDragging(true)
    }
  }

  const handleDragOut = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setDragging(false)
  }

  const handleNewFileUpload = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setDragging(false)

    const draggedFiles = (e.target as HTMLInputElement).files || (e.dataTransfer && e.dataTransfer.files)
    if (draggedFiles && draggedFiles.length > 0) {
      const droppedFile = draggedFiles[0]

      getDetails(fsi.id).then(
        (data) => {
          if (!data.actionFlags.canRequestChange) {
            toast.warning('BROWSER_DROP_TO_ADD_TO_PLACEHOLDER_CANNOT_START_WORKFLOW_MSG')
          } else if (!data.actionFlags.noCRAuthorizers) {
            toast.warning('BROWSER_DROP_TO_ADD_TO_PLACEHOLDER_SOME_AUTHORIZERS_MSG')
          } else {
            showModal(
              <PageWrapper
                fileId={fsi.id}
                title="BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_PLACEHOLDER"
                params={{ file: { id: fsi.id }, fileToUpload: droppedFile }}
                onClose={() => hideModal()}
              />
            )
          }
        },
        (err) => {
          if (err) {
            toast.error('SOMETHING_WENT_WRONG_MSG_ERROR')
          } else {
            toast.warning('MSG_ERROR_FILE_NOT_VALID')
          }
        }
      )

      try {
        if (e.dataTransfer) {
          e.dataTransfer.clearData()
        }
      } catch {
        // Ignore clearData errors (e.g. when dataTransfer is read-only)
      }
    }
  }

  const elementProps = {
    className: (className ? className : '') + (dragging ? ' dndActive' : ''),
    title: title,
    ...dndProps
  }

  const draggingContent = (
    <>
      {dragging && <div className="dnd-information">{t('BROWSER_DROP_TO_ADD_TO_PLACEHOLDER')}</div>}
      {children}
    </>
  )

  return isTableCell ? <td {...elementProps}>{draggingContent}</td> : <div {...elementProps}>{draggingContent}</div>
}

export const TableCellDragNDropFileToPlaceholder = (props: Omit<DragNDropFileToPlaceholderProps, 'isTableCell'>) => (
  <DragNDropFileToPlaceholder {...props} isTableCell />
)
export const DivDragNDropFileToPlaceholder = (props: Omit<DragNDropFileToPlaceholderProps, 'isTableCell'>) => (
  <DragNDropFileToPlaceholder {...props} />
)

export default DragNDropFileToPlaceholder
